/**
 * <td-quick-buy> — in-card quick buy.
 *
 * Owns behaviour only. The variant maths and the hidden [name="id"] input are produced by the
 * vendor <variant-picker> (assets/variant-picker.js), and the add itself is submitted through the
 * vendor <product-form> (assets/main.js). There is no second request to cart/add here. The tray's
 * availability states are computed here, because the vendor 'down' mode matches any one other
 * option, which is wrong once a third option such as Length is in play.
 *
 * Desktop: the tray is revealed by CSS on hover and :focus-within. Mobile: the tray is opened as a
 * modal <dialog>, which gives top-layer rendering (Swiper's transformed wrapper would clip a
 * fixed-position sheet), focus trapping and Escape for free.
 */
if (!customElements.get('td-quick-buy')) {
  const SELECTORS = {
    productForm: 'product-form',
    optionFieldset: '[data-td-quick-buy-option]',
    swatchInput: '[data-td-quick-buy-swatch-input]',
    sizeInput: '[data-td-quick-buy-size-input]',
    optionInput: '[data-td-quick-buy-option-input]',
    sizeState: '[data-td-quick-buy-size-state]',
    card: '[data-td-product-card]',
    cardColor: '[data-td-card-color]',
    cardPrice: '[data-td-card-price]',
    bisTrigger: '[data-td-quick-buy-bis]',
    directAdd: '[data-td-quick-buy-direct]',
  };

  /** Two badges at most on a card, matching the cap in snippets/product-label.liquid. */
  const BADGE_LIMIT = 2;

  /** Web component behind the in-card quick buy swatches, "+" toggle and size tray. */
  class TdQuickBuy extends HTMLElement {
    /** @returns {string} Class applied to `<body>` while the mobile tray is open. */
    static get scrollLockClass() {
      return 'td-quick-buy-locked';
    }

    /** @returns {string} Class applied to the add wrapper while the desktop hover tray is pinned open. */
    static get openClass() {
      return 'td-quick-buy__add--open';
    }

    /** @returns {string} Class applied to a TD card while it shows a colour other than its own. */
    static get colourPreviewClass() {
      return 'td-product-card--colour-preview';
    }

    /**
     * Class applied to a vendor card once the shopper picks a colour, until the pointer leaves:
     * it holds the colour's first image instead of the hover image.
     * @returns {string}
     */
    static get colourHeldClass() {
      return 'product-block--td-colour-held';
    }

    /** @returns {string} Class applied to the direct-add "+" while its add is in flight. */
    static get directLoadingClass() {
      return 'td-quick-buy__toggle--loading';
    }

    /** @returns {string} Class applied to the direct-add "+" while the selected variant is sold out. */
    static get directSoldOutClass() {
      return 'td-quick-buy__toggle--sold-out';
    }

    /**
     * Class applied to the add wrapper when the desktop tray is put away while the pointer or
     * focus is still on it, which :hover and :focus-within would otherwise hold open.
     * @returns {string}
     */
    static get dismissedClass() {
      return 'td-quick-buy__add--dismissed';
    }

    /**
     * Must stay in sync with the matching media query in assets/component-td-quick-add.css.
     * Above it the tray is an inline hover popover; below it, a modal bottom sheet.
     * @returns {string} Media query string.
     */
    static get desktopQuery() {
      return '(min-width: 990px) and (hover: hover) and (pointer: fine)';
    }

    #picker = null;
    #toggle = null;
    #direct = null;
    #tray = null;
    #addWrap = null;
    #form = null;
    #status = null;
    #bisTrigger = null;
    #variantData = null;
    #hiddenIds = null;
    #strings = null;
    #desktopQuery = null;
    #suppressNextAdd = false;
    #colourHeld = false;
    #pendingSizeInput = null;
    #returnFocusTo = null;
    #abort = null;
    #addTimer = null;
    #galleryImages = null;
    #primaryImage = null;
    #initialSwatch = null;
    #colourSlides = new Map();
    #pendingColours = new Set();

    /** @returns {void} */
    connectedCallback() {
      this.#picker = this.querySelector('variant-picker');
      this.#toggle = this.querySelector('[data-td-quick-buy-toggle]');
      this.#tray = this.querySelector('[data-td-quick-buy-tray]');
      this.#addWrap = this.querySelector('[data-td-quick-buy-add]');
      this.#status = this.querySelector('[data-td-quick-buy-status]');
      /* td-selector-exception: .js-product-form is the vendor product-form hook queried by
         assets/main.js and assets/variant-picker.js across the theme. */
      this.#form = this.closest(SELECTORS.productForm)?.querySelector('.js-product-form') || null;

      this.#bisTrigger = this.querySelector(SELECTORS.bisTrigger);
      this.#direct = this.querySelector(SELECTORS.directAdd);

      // Either a "+" with a size tray, or a "+" that adds straight away (one size, or one variant
      // and no picker at all).
      const hasTray = Boolean(this.#picker && this.#toggle && this.#tray);
      if (!this.#form || !this.#addWrap || !(hasTray || this.#direct)) return;

      this.#desktopQuery = window.matchMedia(TdQuickBuy.desktopQuery);
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      this.addEventListener('click', this.#onClick.bind(this), { signal });
      this.addEventListener('keydown', this.#onKeydown.bind(this), { signal });
      this.addEventListener('change', this.#onChange.bind(this), { signal });

      if (hasTray) {
        this.#toggle.addEventListener('click', this.#onToggleClick.bind(this), { signal });
        this.addEventListener('focusin', this.#syncExpanded.bind(this), { signal });
        this.addEventListener('focusout', this.#syncExpanded.bind(this), { signal });
        this.#addWrap.addEventListener('mouseleave', this.#onAddLeave.bind(this), { signal });
        this.#addWrap.addEventListener('focusout', this.#onAddLeave.bind(this), { signal });
        // A pointer coming back to the "+" after a dismissal means the shopper wants the tray again.
        this.#addWrap.addEventListener(
          'mouseenter',
          () => this.#addWrap.classList.remove(TdQuickBuy.dismissedClass),
          { signal }
        );
        this.#tray.addEventListener('close', this.#onDialogClose.bind(this), { signal });
      } else {
        this.#direct.addEventListener('click', this.#onDirectClick.bind(this), { signal });
        this.#syncDirectState();
      }

      const productForm = this.closest(SELECTORS.productForm);
      productForm?.addEventListener('on:cart:add', this.#onCartAdd.bind(this), { signal });
      productForm?.addEventListener('on:cart:error', this.#onCartError.bind(this), { signal });

      // The vendor card builds its slides lazily (window.initLazyScript), so they are scoped to
      // the shown colour at the same first-interaction moment its own lazy image loading uses.
      const productBlock = this.closest('product-block');
      if (productBlock && this.#picker) {
        const scopeOnce = () => {
          if (!this.#galleryImages) this.#scopeGallery(this.querySelector(`${SELECTORS.swatchInput}:checked`));
        };
        productBlock.addEventListener('mouseenter', scopeOnce, { signal, once: true });
        productBlock.addEventListener('touchstart', scopeOnce, { signal, once: true, passive: true });
        productBlock.addEventListener('focusin', scopeOnce, { signal, once: true });
      }

      this.#initialSwatch = this.querySelector(`${SELECTORS.swatchInput}:checked`);
      this.closest(`${SELECTORS.card}, product-block`)?.addEventListener('mouseleave', this.#onCardLeave.bind(this), {
        signal,
      });

      this.#syncTrayStates();
    }

    /** @returns {void} */
    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      window.clearTimeout(this.#addTimer);
      this.#addTimer = null;
      this.#galleryImages = null;
      this.#primaryImage = null;
      this.#initialSwatch = null;
      this.#colourSlides.clear();
      this.#pendingColours.clear();
      this.#closeDialog(this.#tray);
      this.#releaseScroll();
      this.#pendingSizeInput = null;
      this.#returnFocusTo = null;
      this.#variantData = null;
      this.#hiddenIds = null;
      this.#bisTrigger = null;
    }

    /* ----------------------------------------------------------------- tray */

    /** @returns {boolean} Whether the desktop hover-tray breakpoint currently matches. */
    get #isDesktop() {
      return Boolean(this.#desktopQuery?.matches);
    }

    /**
     * @param {MouseEvent} event
     * @returns {void}
     */
    #onToggleClick(event) {
      if (this.#isDesktop) {
        if (this.#addWrap.classList.contains(TdQuickBuy.openClass)) {
          this.#dismissTray();
          return;
        }
        this.#addWrap.classList.remove(TdQuickBuy.dismissedClass);
        this.#addWrap.classList.add(TdQuickBuy.openClass);
        this.#toggle.setAttribute('aria-expanded', 'true');
        this.#focusFirstSize();
        return;
      }

      this.#returnFocusTo = this.#toggle;
      this.#openDialog(this.#tray);
      this.#toggle.setAttribute('aria-expanded', 'true');
      // A keyboard press (detail 0) lands on the first size. A tap focuses the sheet itself:
      // iOS draws the focus ring on script-moved focus, which outlined a size nobody had chosen.
      if (event.detail === 0) {
        this.#focusFirstSize();
      } else {
        this.#tray.focus({ preventScroll: true });
      }
    }

    /** @returns {void} */
    #focusFirstSize() {
      const first =
        this.#tray.querySelector(`${SELECTORS.sizeInput}:not([aria-disabled="true"])`) ||
        this.#tray.querySelector(SELECTORS.sizeInput);
      first?.focus();
    }

    /**
     * @param {HTMLDialogElement} dialog
     * @returns {void}
     */
    #openDialog(dialog) {
      if (!dialog || dialog.open) return;
      dialog.showModal();
      document.body.classList.add(TdQuickBuy.scrollLockClass);
    }

    /**
     * @param {HTMLDialogElement} dialog
     * @returns {void}
     */
    #closeDialog(dialog) {
      if (!dialog?.open) return;
      dialog.close();
    }

    /**
     * @param {Event} evt
     * @returns {void}
     */
    #onDialogClose(evt) {
      this.#releaseScroll();
      if (evt.target === this.#tray && !this.#isDesktop) this.#toggle.setAttribute('aria-expanded', 'false');
      const target = this.#returnFocusTo;
      this.#returnFocusTo = null;
      if (target?.isConnected) target.focus();
    }

    /** @returns {void} */
    #releaseScroll() {
      if (this.#tray?.open) return;
      document.body.classList.remove(TdQuickBuy.scrollLockClass);
    }

    /**
     * Keeps aria-expanded honest for keyboard users, where the CSS hover reveal cannot. Keyboard
     * focus is :focus-visible, which is what reveals the tray; a mouse click leaves focus on the
     * "+" without it.
     */
    #syncExpanded() {
      if (!this.#isDesktop || !this.#tray) return;
      window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.#addWrap) return;
        const { classList } = this.#addWrap;
        const open =
          !classList.contains(TdQuickBuy.dismissedClass) &&
          (classList.contains(TdQuickBuy.openClass) || Boolean(this.#addWrap.querySelector(':focus-visible')));
        this.#toggle.setAttribute('aria-expanded', String(open));
      });
    }

    /**
     * Puts the desktop tray away and returns focus to the "+", which keeps the tray reachable.
     * @returns {void}
     */
    #dismissTray() {
      this.#addWrap.classList.remove(TdQuickBuy.openClass);
      this.#addWrap.classList.add(TdQuickBuy.dismissedClass);
      this.#toggle.setAttribute('aria-expanded', 'false');
      this.#toggle.focus();
    }

    /**
     * Lets the tray reveal again once neither the pointer nor focus is on the add wrapper.
     * @param {MouseEvent|FocusEvent} evt
     * @returns {void}
     */
    #onAddLeave(evt) {
      const focusTarget = evt.type === 'focusout' ? evt.relatedTarget : document.activeElement;
      const focusStays = this.#addWrap.contains(focusTarget);
      const pointerStays = evt.type === 'focusout' && this.#addWrap.matches(':hover');
      if (!focusStays && !pointerStays) this.#addWrap.classList.remove(TdQuickBuy.dismissedClass);
    }

    /**
     * Leaving the card on desktop puts it back as it was: its own colour and a closed tray. A
     * colour picked on a card is a preview, not a choice the page keeps.
     * @returns {void}
     */
    #onCardLeave() {
      if (!this.#isDesktop) return;

      this.#addWrap.classList.remove(TdQuickBuy.openClass, TdQuickBuy.dismissedClass);
      this.#syncExpanded();
      // Cleared before the reset below, so its #showSlides() does not hold the image again.
      this.#colourHeld = false;
      this.closest('product-block')?.classList.remove(TdQuickBuy.colourHeldClass);

      const initial = this.#initialSwatch;
      if (!initial?.isConnected || initial.checked) return;
      initial.checked = true;
      // Through a change event, so assets/variant-picker.js moves the form's variant id back too.
      initial.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* -------------------------------------------------------------- choices */

    /**
     * @param {KeyboardEvent} evt
     * @returns {void}
     */
    #onKeydown(evt) {
      if (evt.key?.startsWith('Arrow') && evt.target.matches?.(SELECTORS.sizeInput)) {
        // Moving through the ladder selects, it does not add.
        this.#suppressNextAdd = true;
      }

      // Mobile closes on Escape natively, as a modal <dialog>. On desktop the tray is shown by
      // hover and focus as well as the pinned class, so Escape dismisses it outright.
      if (evt.key === 'Escape' && this.#isDesktop && this.#tray && this.#addWrap.contains(evt.target)) {
        evt.preventDefault();
        this.#dismissTray();
      }
    }

    /**
     * @param {MouseEvent} evt
     * @returns {void}
     */
    #onClick(evt) {
      // A tap on a modal dialog's backdrop targets the dialog itself, outside its box.
      if (evt.target instanceof HTMLDialogElement && evt.target.open) {
        const rect = evt.target.getBoundingClientRect();
        const outside =
          evt.clientX < rect.left || evt.clientX > rect.right || evt.clientY < rect.top || evt.clientY > rect.bottom;
        if (outside) {
          this.#closeDialog(evt.target);
          return;
        }
      }

      if (evt.target.closest('[data-td-quick-buy-close]')) {
        evt.preventDefault();
        const dialog = evt.target.closest('dialog');
        this.#closeDialog(dialog);
        if (this.#isDesktop && dialog === this.#tray) this.#dismissTray();
        return;
      }

      const sizeInput = evt.target.closest(SELECTORS.sizeInput);
      if (!sizeInput) return;

      if (this.#suppressNextAdd) {
        this.#suppressNextAdd = false;
        return;
      }

      if (sizeInput.getAttribute('aria-disabled') === 'true') {
        evt.preventDefault();
        this.#openBackInStock(this.#findVariant({ size: sizeInput.value })?.id);
        return;
      }

      // A radio's click event fires before its change event, and assets/variant-picker.js only
      // writes the chosen variant into the form's hidden [name="id"] on change. Submitting from
      // here would post the previously selected variant, so the add runs on the next task,
      // after that synchronous update. Re-tapping an already-checked size fires no change, and
      // its id is already correct.
      window.clearTimeout(this.#addTimer);
      this.#addTimer = window.setTimeout(() => {
        this.#addTimer = null;
        this.#addSelectedSize(sizeInput);
      }, 0);
    }

    /**
     * The "+" with nothing to choose adds the form's selected variant: the product's one variant,
     * or the chosen colour's one size. Sold out, it opens back in stock instead, as a sold-out
     * size does. assets/variant-picker.js keeps the form's Add to Cart button disabled for a
     * sold-out variant, and Liquid renders it disabled for a sold-out single-variant product.
     * @returns {void}
     */
    #onDirectClick() {
      const variantId = Number(this.#form.querySelector('[name="id"]')?.value);
      if (!variantId) return;

      if (this.#form.querySelector('[name="add"]')?.disabled) {
        this.#openBackInStock(variantId);
        return;
      }

      this.#direct.classList.add(TdQuickBuy.directLoadingClass);
      this.#submit();
    }

    /**
     * Greys the direct "+" while the selected variant is sold out, as the tray greys a sold-out
     * size. It stays operable while there is a back-in-stock signup to open; without one it is
     * aria-disabled, and a press only announces sold out. Runs after assets/variant-picker.js has
     * updated the form's Add to Cart button, which is what #onDirectClick() reads too.
     * @returns {void}
     */
    #syncDirectState() {
      if (!this.#direct) return;
      const soldOut = Boolean(this.#form.querySelector('[name="add"]')?.disabled);
      this.#direct.classList.toggle(TdQuickBuy.directSoldOutClass, soldOut);
      this.#direct.toggleAttribute('aria-disabled', soldOut && !this.#bisTrigger);
    }

    /**
     * @param {HTMLInputElement} sizeInput
     * @returns {void}
     */
    #addSelectedSize(sizeInput) {
      const idInput = this.#form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;

      this.#pendingSizeInput = sizeInput;
      sizeInput.nextElementSibling?.classList.add('td-quick-buy__size--loading');
      this.#submit();
    }

    /**
     * Submits through the vendor <product-form>, which answers with on:cart:add or on:cart:error.
     * @returns {void}
     */
    #submit() {
      if (typeof this.#form.requestSubmit === 'function') {
        this.#form.requestSubmit();
      } else {
        this.#form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }

    /** @returns {void} */
    #onCartAdd() {
      const label = this.#pendingSizeInput?.nextElementSibling;
      label?.classList.remove('td-quick-buy__size--loading');
      label?.classList.add('td-quick-buy__size--added');
      window.setTimeout(() => label?.classList.remove('td-quick-buy__size--added'), 2000);

      this.#announce(
        [this.dataset.tdProductTitle, this.#pendingSizeInput?.value, this.#t('added')]
          .filter(Boolean)
          .join(' · ')
      );

      this.#pendingSizeInput?.focus();
      this.#pendingSizeInput = null;
      this.#direct?.classList.remove(TdQuickBuy.directLoadingClass);
      if (this.#tray?.open) this.#closeDialog(this.#tray);
    }

    /** @returns {void} */
    #onCartError() {
      this.#pendingSizeInput?.nextElementSibling?.classList.remove('td-quick-buy__size--loading');
      this.#pendingSizeInput = null;
      this.#direct?.classList.remove(TdQuickBuy.directLoadingClass);
    }

    /* -------------------------------------------------------- availability */

    /**
     * Change bubbles here after the vendor picker has resolved the variant. Only a shopper's own
     * change is announced: the reset when the pointer leaves the card is synthetic.
     * @param {Event} evt
     * @returns {void}
     */
    #onChange(evt) {
      const input = evt.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.matches(SELECTORS.swatchInput)) {
        // Only a shopper's pick holds the image; the reset on leaving the card is not trusted. The
        // hold itself goes on with the new images in #showSlides(): put on here, it would hide the
        // hover image while the colour loads and show the old colour's first image meanwhile.
        if (evt.isTrusted) this.#colourHeld = true;
        this.#syncTrayStates();
        this.#syncDirectState();
        this.#updateCard(input);
        if (evt.isTrusted) this.#announce([input.value, this.#t('sizes_updated')].filter(Boolean).join(' · '));
      } else if (input.matches(SELECTORS.optionInput)) {
        this.#syncTrayStates();
        this.#announce([input.value, this.#t('sizes_updated')].filter(Boolean).join(' · '));
      }
    }

    /**
     * The tray's option fieldsets by role ('color', 'size', 'extra'), each with its option index
     * and checked value.
     * @returns {Record<string, {index: number, value: string|null}>}
     */
    #readSelections() {
      const selections = {};
      this.querySelectorAll(SELECTORS.optionFieldset).forEach((fieldset) => {
        selections[fieldset.dataset.tdQuickBuyOption] = {
          index: Number(fieldset.dataset.tdOptionIndex),
          value: fieldset.querySelector('input:checked')?.value ?? null,
        };
      });
      return selections;
    }

    /** @returns {Set<number>} Variants hidden from this shopper, from the Liquid-resolved list. */
    #getHiddenIds() {
      this.#hiddenIds ??= new Set(
        (this.#picker?.dataset.tdHiddenVariants || '').split('|').filter(Boolean).map(Number)
      );
      return this.#hiddenIds;
    }

    /**
     * Greys every size and further option that the selected colour (and, for sizes, the selected
     * further option) cannot supply. Sold-out and missing sizes stay visible, focusable and
     * aria-disabled; a sold-out further option stays selectable so its sizes can be reached for
     * back in stock.
     * @returns {void}
     */
    #syncTrayStates() {
      const variants = this.#getVariantData()?.variants;
      if (!variants) return;

      const { color, size, extra } = this.#readSelections();
      const hidden = this.#getHiddenIds();
      const pool = variants.filter(
        (variant) => !hidden.has(variant.id) && (!color || variant.options[color.index] === color.value)
      );
      const stateFor = (index, value, constraint) => {
        const matches = pool.filter(
          (variant) => variant.options[index] === value && (!constraint || variant.options[constraint.index] === constraint.value)
        );
        return { exists: matches.length > 0, available: matches.some((variant) => variant.available) };
      };

      if (size) {
        this.querySelectorAll(SELECTORS.sizeInput).forEach((input) => {
          const { exists, available } = stateFor(size.index, input.value, extra);
          input.classList.toggle('is-unavailable', !available);
          input.classList.toggle('is-nonexistent', !exists);
          if (available) {
            input.removeAttribute('aria-disabled');
          } else {
            input.setAttribute('aria-disabled', 'true');
          }
          this.#setState(input, available, exists && Boolean(this.#bisTrigger));
        });
      }

      if (extra) {
        this.querySelectorAll(SELECTORS.optionInput).forEach((input) => {
          const { available } = stateFor(extra.index, input.value);
          input.toggleAttribute('data-td-sold-out', !available);
          this.#setState(input, available);
        });
      }
    }

    /**
     * @param {HTMLInputElement} input
     * @param {boolean} available
     * @param {boolean} [notify] Whether choosing it opens the back-in-stock signup.
     * @returns {void}
     */
    #setState(input, available, notify = false) {
      const state = input.nextElementSibling?.querySelector(SELECTORS.sizeState);
      if (state) state.textContent = available ? '' : ` ${this.#t(notify ? 'sold_out_notify' : 'sold_out')}`;
    }

    /**
     * The variant matching the tray's current selections, with any overrides by role.
     * @param {Record<string, string>} [overrides]
     * @returns {object|undefined}
     */
    #findVariant(overrides = {}) {
      const selections = Object.entries(this.#readSelections());
      return this.#getVariantData()?.variants?.find((variant) =>
        selections.every(([role, { index, value }]) => variant.options[index] === (overrides[role] ?? value))
      );
    }

    /** @returns {object|null} Parsed variant JSON from the vendor variant-picker data island. */
    #getVariantData() {
      if (this.#variantData) return this.#variantData;
      const dataEl = this.#picker?.querySelector('[type="application/json"]');
      if (!dataEl) return null;
      try {
        this.#variantData = JSON.parse(dataEl.textContent);
      } catch {
        this.#variantData = null;
      }
      return this.#variantData;
    }

    /* ------------------------------------------------------- colour changes */

    /**
     * Moves the whole card to the chosen colour: links, title colour, price, badges and image.
     * No network request.
     * @param {HTMLInputElement} swatchInput
     * @returns {void}
     */
    #updateCard(swatchInput) {
      const card = this.closest(SELECTORS.card) || this.closest('product-block');
      if (!card) return;

      const variantId = Number(swatchInput.dataset.tdVariantId);
      const variant = this.#getVariantData()?.variants?.find((v) => v.id === variantId);
      if (!variant) return;

      // The path is kept as rendered, so a collection-scoped URL keeps its collection.
      const productUrl = this.dataset.tdProductUrl;
      card.querySelectorAll('a[href]').forEach((link) => {
        const path = link.getAttribute('href').split('?')[0];
        if (!path.endsWith(productUrl)) return;
        link.setAttribute('href', `${path}?variant=${variant.id}`);
      });

      const colorName = card.querySelector(SELECTORS.cardColor);
      if (colorName) colorName.textContent = swatchInput.value;

      this.#updatePrice(card, variant);
      this.#updateBadges(card, swatchInput.value);

      // The vendor card keeps its own slides, so it is re-scoped to the chosen colour rather than
      // having its <img> swapped — that also moves the hover image and swipe to the colour.
      if (this.#scopeGallery(swatchInput, variant)) return;

      // The TD card shows another colour's featured image in place of its own. Its hover image
      // belongs to its own colour, so it steps aside meanwhile, or it would cover the change.
      const preview = swatchInput !== this.#initialSwatch;
      card.classList.toggle(TdQuickBuy.colourPreviewClass, preview);
      if (preview) {
        /* td-selector-exception: the TD card image comes from the shared vendor 'image' snippet,
           which takes no attribute passthrough, so it has no data-td-* hook. */
        this.#swapImage(card.querySelector('.td-product-card__image--main'), variant.featured_image, () => swatchInput.checked);
      } else {
        this.#restorePrimaryImage();
      }
    }

    /**
     * Rewrites the card price for the chosen colour the way assets/variant-picker.js does on the
     * product page, keeping the Intelligems hooks (igPrice / igComparePrice) in place.
     * @param {HTMLElement} card
     * @param {object} variant
     * @returns {void}
     */
    #updatePrice(card, variant) {
      const formatted = this.#getVariantData()?.formatted?.[variant.id];
      /* td-selector-exception: vendor snippets/price.liquid markup inside the TD price hook. */
      const price = card.querySelector(`${SELECTORS.cardPrice} .price`);
      const current = price?.querySelector('.price__current');
      if (!formatted || !current) return;

      current.innerHTML = `<span class="igPrice">${formatted.price}</span>`;

      let was = price.querySelector('.price__was');
      if (formatted.compareAtPrice) {
        if (!was) {
          was = document.createElement('span');
          was.className = 'price__was igComparePrice';
          current.after(was);
        }
        was.innerHTML = formatted.compareAtPrice;
      } else {
        was?.remove();
      }

      price.classList.toggle('price--on-sale', Boolean(formatted.compareAtPrice));
      price.dataset.variantId = String(variant.id);
      // One colour has one price, so a product-wide "From" no longer applies.
      price.querySelector('.price__from')?.setAttribute('hidden', '');
    }

    /**
     * Shows the chosen colour's stock and sale badges, within the two-badge cap.
     * @param {HTMLElement} card
     * @param {string} colour
     * @returns {void}
     */
    #updateBadges(card, colour) {
      /* td-selector-exception: vendor snippets/product-label.liquid renders one label per colour,
         keyed by data-color-label and hidden with td-hide; it has no data-td-* hook. */
      const colourLabels = [...card.querySelectorAll('.product-label[data-color-label]')];
      if (!colourLabels.length) return;

      const fixed = card.querySelectorAll('.product-label:not([data-color-label]):not(.td-hide)').length;
      let room = Math.max(0, BADGE_LIMIT - fixed);
      colourLabels.forEach((label) => {
        const show = room > 0 && label.dataset.colorLabel === colour;
        if (show) room -= 1;
        label.classList.toggle('td-hide', !show);
      });
    }

    /**
     * Confines the vendor product-block gallery to one colour's slides. The vendor card renders
     * every slide it has into one list (the product media plus each colour's variant-gallery
     * files, ids "mf-<colour handle>-<n>" from snippets/td-card-gallery-secondary.liquid), so
     * paging walked through every colourway. Its paging, swipe and hover image all read
     * `this.images`, so scoping that array is enough; no vendor code changes. A card split one
     * per colour only carries its own colour's slides, so another colour's are fetched once.
     * @param {HTMLInputElement|null} swatchInput Checked colour swatch, when the card has a row.
     * @param {object} [variant] Variant for that colour, when already resolved.
     * @returns {boolean} Whether the gallery was scoped.
     */
    #scopeGallery(swatchInput, variant) {
      const card = this.closest('product-block');
      if (!card) return false;

      const resolved = swatchInput
        ? variant || this.#getVariantData()?.variants?.find((v) => v.id === Number(swatchInput.dataset.tdVariantId))
        : null;

      // Before the card's lazy init (window.initLazyScript) there is no slide list to scope, so
      // only its first image follows the colour; the slides are scoped on the next interaction.
      if (typeof card.setActiveImage !== 'function' || !Array.isArray(card.images)) {
        if (resolved) this.#matchPrimaryImage(card, resolved, () => swatchInput.checked);
        return true;
      }

      this.#galleryImages ??= [...card.images];
      if (!swatchInput) {
        this.#showSlides(card, this.#galleryImages);
        return true;
      }

      const handle = swatchInput.dataset.tdColorHandle;
      const prefix = `mf-${handle}-`;
      const featuredId = String(resolved?.featured_media?.id);
      const ownSlides = this.#galleryImages.filter(
        (img) => img.dataset.mediaId === featuredId || img.dataset.mediaId?.startsWith(prefix)
      );
      let slides = ownSlides.length ? ownSlides : this.#colourSlides.get(handle);
      // The card's own colour always has its rendered slides, matched or not.
      if (!slides && swatchInput === this.#initialSwatch) slides = this.#galleryImages;

      if (slides) {
        // The first slide is put back only when it is about to show. Restored while it fades out,
        // it would flash the card's own colour over the chosen one.
        if (slides.includes(this.#getPrimarySlide(card))) this.#restorePrimaryImage();
        this.#showSlides(card, slides);
        return true;
      }

      // The colour's featured image stands in on the first slide until its own slides arrive. It
      // goes in once loaded, and only if the shopper is still on that colour and the slides have
      // not arrived first.
      this.#swapPrimaryImage(
        card,
        resolved?.featured_image,
        () => swatchInput.checked && !this.#colourSlides.has(handle)
      ).then((swapped) => {
        if (swapped) this.#showSlides(card, this.#galleryImages.slice(0, 1));
      });
      this.#fetchColourSlides(card, swatchInput, resolved);
      return true;
    }

    /**
     * @param {HTMLElement} card
     * @param {HTMLElement[]} slides
     * @returns {void}
     */
    #showSlides(card, slides) {
      card.images = slides;
      card.setActiveImage(0);
      // In the same frame as the new slides, so the old hover image crossfades straight to them.
      if (this.#colourHeld) card.classList.add(TdQuickBuy.colourHeldClass);
    }

    /**
     * Fetches a colour's slides once, from this card rendered for that colour by the
     * td-recently-viewed-card section (the renderer the recently viewed carousel uses), and adds
     * them to the card's slide list. Only a card that can page its slides takes them.
     * @param {HTMLElement} card
     * @param {HTMLInputElement} swatchInput
     * @param {object|undefined} variant
     * @returns {Promise<void>}
     */
    async #fetchColourSlides(card, swatchInput, variant) {
      const handle = swatchInput.dataset.tdColorHandle;
      /* td-selector-exception: the vendor product-block paging layout, which only cards with a
         second image get; its slide styles are what keep the added slides stacked. */
      if (!variant || this.#pendingColours.has(handle) || !card.querySelector('.image-cont--with-secondary-image')) {
        return;
      }
      this.#pendingColours.add(handle);

      try {
        const url = new URL(this.dataset.tdProductUrl, window.location.origin);
        url.searchParams.set('variant', String(variant.id));
        url.searchParams.set('section_id', 'td-recently-viewed-card');
        const response = await fetch(url, { signal: this.#abort?.signal });
        if (!response.ok) throw new Error(`Quick buy colour slides request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const container = this.#getPrimarySlide(card)?.parentElement;
        /* td-selector-exception: vendor product-block slide classes, as ProductBlock in
           assets/main.js reads and toggles them. Every fetched slide is stacked as a secondary
           slide; the card's own first slide keeps its height. */
        const slides = [...html.querySelectorAll('.product-block__image')].map((slide) => {
          slide.classList.remove(
            'product-block__image--primary',
            'product-block__image--active',
            'product-block__image--show-on-hover',
            'product-block__image--inactivated'
          );
          slide.classList.add('product-block__image--secondary');
          return slide;
        });
        if (!slides.length || !container || !this.isConnected) return;

        container.append(...slides);
        // Cached and shown only once the first image has loaded, so the fade never lands on an
        // empty slide.
        await TdQuickBuy.loadSlides(slides);
        if (!this.isConnected) return;
        this.#colourSlides.set(handle, slides);

        // Shown only if the shopper is still on that colour. The first slide keeps its stand-in
        // image while it fades out; #scopeGallery() restores it when it next shows.
        if (swatchInput.checked) this.#showSlides(card, slides);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn(error);
      } finally {
        this.#pendingColours.delete(handle);
      }
    }

    /**
     * @param {HTMLElement} card
     * @returns {HTMLElement|null} The card's first slide, which holds its own colour's image.
     */
    #getPrimarySlide(card) {
      /* td-selector-exception: the vendor product-block first slide, which ProductBlock in
         assets/main.js reads by class. */
      return this.#galleryImages?.[0] || card.querySelector('.product-block__image--primary');
    }

    /**
     * Restores the first slide for its own colour, or shows another colour's featured image in it.
     * @param {HTMLElement} card
     * @param {object} variant
     * @param {() => boolean} isCurrent Whether the swap is still wanted once the image has loaded.
     * @returns {void}
     */
    #matchPrimaryImage(card, variant, isCurrent) {
      if (this.#getPrimarySlide(card)?.dataset.mediaId === String(variant.featured_media?.id)) {
        this.#restorePrimaryImage();
      } else {
        this.#swapPrimaryImage(card, variant.featured_image, isCurrent);
      }
    }

    /**
     * Shows another colour's featured image in the card's first slide, keeping the original.
     * @param {HTMLElement} card
     * @param {object|undefined} image Variant featured_image.
     * @param {() => boolean} isCurrent Whether the swap is still wanted once the image has loaded.
     * @returns {Promise<boolean>} Whether the image was swapped.
     */
    #swapPrimaryImage(card, image, isCurrent) {
      return this.#swapImage(this.#getPrimarySlide(card)?.querySelector('img'), image, isCurrent);
    }

    /**
     * Shows another colour's featured image in a card's own image, keeping the original once so
     * #restorePrimaryImage() can put it back. The image is loaded off-screen first and swapped in
     * from cache, so the card never shows an empty frame while it downloads.
     * @param {HTMLImageElement|null} img
     * @param {object|undefined} image Variant featured_image.
     * @param {() => boolean} isCurrent Whether the swap is still wanted once the image has loaded.
     * @returns {Promise<boolean>} Whether the image was swapped.
     */
    async #swapImage(img, image, isCurrent) {
      if (!img || !image?.src) return false;

      await TdQuickBuy.preloadImage(img, image);
      if (!img.isConnected || !isCurrent()) return false;

      if (!this.#primaryImage) {
        this.#primaryImage = {
          img,
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          alt: img.getAttribute('alt'),
        };
        // The card's images are cropped to one shape for the page; the stand-in is not, and this
        // <img> sets the card's image height. Its width and height attributes keep the cropped
        // shape, so the card holds still and object-fit: cover frames the stand-in inside it.
        const width = img.getAttribute('width');
        const height = img.getAttribute('height');
        if (width && height) img.style.setProperty('--td-quick-buy-image-ratio', `${width} / ${height}`);
      }
      TdQuickBuy.applyImage(img, image);
      return true;
    }

    /** @returns {void} */
    #restorePrimaryImage() {
      if (!this.#primaryImage) return;
      const { img, src, srcset, alt } = this.#primaryImage;
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      img.setAttribute('alt', alt || '');
      img.style.removeProperty('--td-quick-buy-image-ratio');
      this.#primaryImage = null;
    }

    /**
     * Points an <img> at a Shopify image, keeping the widths its srcset already offers.
     * @param {HTMLImageElement|null} img
     * @param {object|undefined} image Variant featured_image ({ src, alt }).
     * @param {HTMLImageElement|null} [template] The <img> whose srcset widths and size to follow.
     * @returns {void}
     */
    static applyImage(img, image, template = img) {
      if (!img || !image?.src) return;

      const widths = (template.getAttribute('srcset') || '')
        .split(',')
        .map((part) => parseInt(part.trim().split(' ')[1], 10))
        .filter((width) => Number.isFinite(width));

      if (widths.length) {
        img.setAttribute('srcset', widths.map((w) => `${TdQuickBuy.withWidth(image.src, w)} ${w}w`).join(', '));
      }
      img.setAttribute('src', TdQuickBuy.withWidth(image.src, template.width || 480));
      img.setAttribute('alt', image.alt || '');
    }

    /**
     * Loads a Shopify image off-screen at the candidate the card's <img> would pick, so swapping it
     * in afterwards is a cache hit. A failed load resolves too; the swap then loads it as before.
     * @param {HTMLImageElement} img
     * @param {object} image Variant featured_image.
     * @returns {Promise<void>}
     */
    static preloadImage(img, image) {
      const probe = new Image();
      // sizes before srcset, so the probe picks the same width as the card.
      probe.sizes = img.getAttribute('sizes') || '';
      TdQuickBuy.applyImage(probe, image, img);
      return probe.decode().catch(() => {});
    }

    /**
     * Loads a colour's first slide, which it opens on, and starts its second, the desktop hover
     * image. Fetched slides arrive lazy; appended out of view, they would stay empty until shown.
     * @param {HTMLElement[]} slides
     * @returns {Promise<void>}
     */
    static loadSlides(slides) {
      const [first, hover] = slides.map((slide) => slide.querySelector('img'));
      if (hover) hover.loading = 'eager';
      if (!first) return Promise.resolve();
      first.loading = 'eager';
      return first.decode().catch(() => {});
    }

    /**
     * @param {string} src
     * @param {number} width
     * @returns {string}
     */
    static withWidth(src, width) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch {
        return src;
      }
    }

    /* ------------------------------------------------------- back in stock */

    /**
     * Opens the AMP Back in Stock signup on the chosen sold-out variant. The app widget handles
     * clicks on a .BIS_trigger document-wide and reads its data-variant-id (the app's attribute
     * name), so the signup, its copy and its focus handling all belong to AMP. Without the widget
     * (app embed off, script blocked) the variant just stays sold out.
     * @param {number|undefined} variantId
     * @returns {void}
     */
    #openBackInStock(variantId) {
      if (!this.#bisTrigger || !variantId || this.#getHiddenIds().has(variantId) || !window.BIS) {
        this.#announce(this.#t('sold_out'));
        return;
      }

      // The mobile sheet is a modal <dialog> in the top layer, which would cover AMP's popup.
      this.#closeDialog(this.#tray);
      this.#bisTrigger.dataset.variantId = String(variantId);
      this.#bisTrigger.click();
    }

    /* ------------------------------------------------------------- helpers */

    /**
     * @param {string} message
     * @returns {void}
     */
    #announce(message) {
      if (this.#status) this.#status.textContent = message;
    }

    /**
     * Storefront copy travels with the component rather than through a global, so no layout edit.
     * Liquid's t filter HTML-escapes its output ("You&#39;re"), so each string is decoded once
     * before it is written as text.
     * @param {string} key
     * @returns {string}
     */
    #t(key) {
      if (!this.#strings) {
        const el = this.querySelector('[data-td-quick-buy-strings]');
        try {
          this.#strings = el ? JSON.parse(el.textContent) : {};
        } catch {
          this.#strings = {};
        }
      }
      const value = this.#strings[key] || '';
      return value && new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
    }
  }

  customElements.define('td-quick-buy', TdQuickBuy);
}
